import { useState } from 'react';
import { X, Search } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useCollaboratorsForMessaging, useGetOrCreateDm } from '../../hooks/queries/useMessaging';
import { CollaboratorOptionRow } from '../collaborators/CollaboratorSelect';

interface NewDmModalProps {
  onClose: () => void;
  onCreated: (channelId: string) => void;
}

export function NewDmModal({ onClose, onCreated }: NewDmModalProps) {
  const [search, setSearch] = useState('');
  const { data: collaborators = [], isLoading } = useCollaboratorsForMessaging();
  const getOrCreateDm = useGetOrCreateDm();

  const filtered = collaborators.filter((c) =>
    c.full_name.toLowerCase().includes(search.toLowerCase()),
  );

  function handleSelect(collaboratorId: string) {
    getOrCreateDm.mutate(collaboratorId, {
      onSuccess: (channel) => onCreated(channel.id),
    });
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div className="bg-cime-surface rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-cime-border">
          <h2 className="font-heading text-lg font-semibold text-cime-text-primary">
            Nouveau message direct
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-cime-sidebar-active-bg text-cime-text-muted transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4">
          <div className="flex items-center gap-2 border border-cime-border rounded-lg px-3 py-2 bg-cime-bg focus-within:ring-2 focus-within:ring-cime-primary/30 focus-within:border-cime-primary transition-colors mb-3">
            <Search className="w-4 h-4 text-cime-text-muted flex-shrink-0" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un collaborateur…"
              className="flex-1 bg-transparent text-sm text-cime-text-primary placeholder:text-cime-text-muted focus:outline-none"
              autoFocus
            />
          </div>

          <div className="max-h-64 overflow-y-auto">
            {isLoading ? (
              <p className="text-sm text-cime-text-muted text-center py-4">Chargement…</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-cime-text-muted text-center py-4">Aucun collaborateur trouvé</p>
            ) : (
              filtered.map((collab) => (
                <button
                  key={collab.id}
                  type="button"
                  disabled={getOrCreateDm.isPending}
                  onClick={(e) => { e.stopPropagation(); handleSelect(collab.id); }}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left rounded-lg',
                    'hover:bg-cime-sidebar-active-bg transition-colors',
                    getOrCreateDm.isPending && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  <CollaboratorOptionRow option={collab} size="sm" />
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
